<template>
  <template v-if="!isProductShippingLoaded">
    <div>
      <sib-skeleton-loading
        v-for="index in 3"
        :key="index"
      ></sib-skeleton-loading>
    </div>
  </template>
  <template v-else>
    <div class="product__shipping-timeline mt-md mb-sm">
      <div
        class="timeline__item"
        :class="{ 'timeline__item-note': timeline.isNote }"
        v-for="(timeline, index) of timelines"
        :key="index"
      >
        <sib-image
          class="timeline__image"
          :width="24"
          :height="24"
          :src="timeline.icon"
          :alt="timeline.title"
          v-if="timeline.icon"
        ></sib-image>
        <span class="timeline__date" v-if="timeline.date">
          {{ timeline.date }}
        </span>
        <span class="timeline__title">
          {{ timeline.title }}
        </span>
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { dateFormatShortMonthAndDay } from '@/utils/date'
import { useProductShipping } from './composables/useProductShipping'
import { computed } from 'vue'
import useProductStore from '@/store/product'
import { ProductShipping } from '@/types'
import { US_COUNTRY_CODE } from '@/utils'
import { themeData } from '@/themes'
import { isProductHasTags } from '@/utils/product'

const props = defineProps<{
  productShipping?: ProductShipping
}>()

const productStore = useProductStore()
const { isShippedFromUS, isProductShippingLoaded } = useProductShipping()
const shippedFromUS = computed(() => {
  if (props.productShipping) {
    return props.productShipping.ship_from === US_COUNTRY_CODE
  }
  return isShippedFromUS.value
})

const isIncreaseProcessing = (
    isProductHasTags(productStore.product, ['AI_removebg']) ||
    isProductHasTags(productStore.product, ['AI_QRMusic']) ||
    isProductHasTags(productStore.product, ['AI_Music'])
  )
const timelines = computed(() => {
  if (!isProductShippingLoaded.value) return []
  let { maximumShipping, minimumShipping, processing } =
    productStore.productShippingTime
  if (props.productShipping) {
    maximumShipping = props.productShipping.maximum_shipping || 0
    minimumShipping = props.productShipping.minimum_shipping || 0
    processing = props.productShipping.processing || 0
  }

  const date = new Date()
  const orderPlacedDate = dateFormatShortMonthAndDay(date)
  if (isIncreaseProcessing) {
    date.setDate(date.getDate() + processing + 3)
  } else {
    date.setDate(date.getDate() + processing)
  }
  const processingDate = dateFormatShortMonthAndDay(date)
  date.setDate(date.getDate() + minimumShipping)
  const minimumShippingDate = dateFormatShortMonthAndDay(date)
  date.setDate(date.getDate() + maximumShipping - minimumShipping)
  const maximumShippingDate = dateFormatShortMonthAndDay(date)

  return [
    {
      icon: themeData('iconOrderPlaced'),
      date: orderPlacedDate,
      title: 'Order Placed',
    },
    {
      title: 'In Production',
      isNote: true,
    },
    {
      icon: shippedFromUS.value
        ? themeData('iconDomestic')
        : themeData('iconPlane'),
      date: processingDate,
      title: 'Order Ships',
    },
    {
      title: 'In Transit',
      isNote: true,
    },
    {
      icon: themeData('iconDelivered'),
      date: `${minimumShippingDate} ~ ${maximumShippingDate}`,
      title: 'Delivered',
    },
  ]
})
</script>

<style lang="scss">
.product__shipping-timeline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  &::before {
    content: ' ';
    display: block;
    width: 70%;
    height: 0;
    border-bottom: dashed 1px var(--text-color-secondary);
    position: absolute;
    top: 12px;
    left: 15%;
  }

  .timeline {
    &__item {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      z-index: 1;

      &:last-child {
        padding-left: 0.5em;
      }

      &:nth-child(3) {
        margin-left: 2em;
      }
    }

    &__item-note {
      position: relative;
      overflow: visible;
      align-self: flex-start;
      z-index: 10;
      .timeline__title {
        position: absolute;
        top: -5px;
        white-space: nowrap;
        transform: translateX(1em);
      }
    }

    &__image {
      width: 48px;
      height: 24px;
      object-fit: contain;
      margin-bottom: 0.5em;
      text-align: center;
      background-color: #fff;
      @include media-md-up {
        width: 64px;
      }
    }

    &__date {
      font-weight: 600;
    }

    &__title {
      font-size: 12px;
      color: var(--text-color-secondary);
    }
  }
}
</style>
